import { hashKey, skipToken, useQuery } from '@tanstack/react-query'

import {
  type EvidenceScope,
  getPrereadEvidenceStats,
  getPrereadFeedback,
  getPrereadRatingStats,
  listPrereadFeedback,
  type PrereadFilters,
  type PrereadRatingBucket,
  type PrereadReportFilters,
} from '@/api/prereadFeedback'
import { shouldRetry } from '@/pages/app/ReportedMeetings/useReportedMeetings'

const options = {
  retry: shouldRetry,
  staleTime: 60_000,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
}

export const usePrereadReports = (filters: PrereadReportFilters, cursor: string | undefined, enabled: boolean) =>
  useQuery({
    ...options,
    queryKey: ['preread-feedback', 'list', filters, cursor ?? null] as const,
    placeholderData: (previousData: Awaited<ReturnType<typeof listPrereadFeedback>> | undefined, previousQuery) => {
      return hashKey([previousQuery?.queryKey[2]]) === hashKey([filters]) ? previousData : undefined
    },
    queryFn: ({ signal }) => listPrereadFeedback({ ...filters, cursor }, signal),
    enabled,
  })
export const usePrereadRatingStats = (filters: PrereadReportFilters, bucket: PrereadRatingBucket, enabled: boolean) =>
  useQuery({
    ...options,
    queryKey: ['preread-feedback', 'ratings', filters, bucket],
    queryFn: ({ signal }) => getPrereadRatingStats({ ...filters, bucket }, signal),
    enabled,
  })
export const usePrereadStats = (
  filters: PrereadFilters,
  scope: EvidenceScope,
  bucket: PrereadRatingBucket,
  enabled: boolean
) =>
  useQuery({
    ...options,
    queryKey: ['preread-feedback', 'stats', filters, scope, bucket],
    queryFn: ({ signal }) => getPrereadEvidenceStats({ ...filters, scope, bucket }, signal),
    enabled,
  })
export const usePrereadReport = (id: string | null, enabled: boolean) =>
  useQuery({
    ...options,
    queryKey: ['preread-feedback', 'detail', id],
    queryFn: id === null ? skipToken : ({ signal }) => getPrereadFeedback(id, signal),
    enabled: enabled && id !== null,
  })
